/**
 * Bank-statement parsing, pure and on-device. Input is a grid of cells (CSV text or an Excel sheet already read);
 * output is transactions in integer minor units. No I/O, no `Date`: dates are calendar strings (CLAUDE.md rule 3)
 * and money is built from the digits of the cell, never from float arithmetic (rule 1).
 *
 * Indian statements put dates as dd/mm/yyyy, dd/mm/yy, dd-MM-yyyy or dd-MMM-yy and amounts with commas ("1,23,456.50").
 * Month-first dates are not supported: a date like 03/04/2025 is 3 April.
 */

export type Cell = string | number | null;
export type Grid = Cell[][];

export interface ColumnMapping {
  headerRow: number;
  date: number;
  narration: number;
  ref: number | null;
  debit: number | null;
  credit: number | null;
  amount: number | null;
  drcr: number | null;
  balance: number | null;
}

export type Direction = "debit" | "credit";

export interface Txn {
  /** Stable within one file: the sheet row, 1-based. */
  id: string;
  rowNo: number;
  /** YYYY-MM-DD */
  date: string;
  description: string;
  ref: string | null;
  direction: Direction;
  /** Always above zero, in minor units. */
  amount: number;
  balance: number | null;
}

export interface BadRow {
  rowNo: number;
  reason: string;
  /** The row as text, for the "couldn't read" list. */
  raw: string;
}

// ---- cells ------------------------------------------------------------------------------------

const textOf = (c: Cell | undefined): string => (c === null || c === undefined ? "" : String(c).replace(/ /g, " ").trim());
const isEmpty = (c: Cell | undefined) => textOf(c) === "";

// ---- dates --------------------------------------------------------------------------------------

const MONTHS: Record<string, number> = { jan: 1, feb: 2, mar: 3, apr: 4, may: 5, jun: 6, jul: 7, aug: 8, sep: 9, oct: 10, nov: 11, dec: 12 };

const isLeap = (y: number) => (y % 4 === 0 && y % 100 !== 0) || y % 400 === 0;
const daysIn = (y: number, m: number) => (m === 2 ? (isLeap(y) ? 29 : 28) : [4, 6, 9, 11].includes(m) ? 30 : 31);
const pad = (n: number, w = 2) => String(n).padStart(w, "0");

function iso(y: number, m: number, d: number): string | null {
  if (y < 1990 || y > 2100 || m < 1 || m > 12 || d < 1 || d > daysIn(y, m)) return null;
  return `${pad(y, 4)}-${pad(m)}-${pad(d)}`;
}

/** dd/mm/yyyy, dd/mm/yy, dd-mm-yyyy, dd.mm.yyyy, dd-MMM-yy, dd MMM yyyy, d-Month-yyyy and ISO yyyy-mm-dd, with an optional time after. Else null. */
export function parseDate(cell: Cell | undefined): string | null {
  const t = textOf(cell).replace(/[T\s]+\d{1,2}:\d{2}(:\d{2})?(\s*[AaPp][Mm])?.*$/, "");
  if (t === "") return null;
  const isoM = /^(\d{4})-(\d{2})-(\d{2})$/.exec(t);
  if (isoM) return iso(+isoM[1]!, +isoM[2]!, +isoM[3]!);
  const m = /^(\d{1,2})[/\-. ]+([A-Za-z]{3,9}|\d{1,2})[/\-.,\s]+(\d{4}|\d{2})$/.exec(t);
  if (!m) return null;
  const day = +m[1]!;
  const month = /^\d+$/.test(m[2]!) ? +m[2]! : MONTHS[m[2]!.slice(0, 3).toLowerCase()];
  if (!month) return null;
  const rawYear = m[3]!;
  const year = rawYear.length === 2 ? (+rawYear < 70 ? 2000 : 1900) + +rawYear : +rawYear;
  return iso(year, month, day);
}

/** Excel serial day number → YYYY-MM-DD, integer arithmetic only. */
export function dateFromSerial(serial: number): string | null {
  if (!Number.isFinite(serial) || serial < 1) return null;
  // days since 1970-01-01, then Howard Hinnant's civil_from_days
  const z = Math.floor(serial) - 25569 + 719468;
  const era = Math.floor(z / 146097);
  const doe = z - era * 146097;
  const yoe = Math.floor((doe - Math.floor(doe / 1460) + Math.floor(doe / 36524) - Math.floor(doe / 146096)) / 365);
  const doy = doe - (365 * yoe + Math.floor(yoe / 4) - Math.floor(yoe / 100));
  const mp = Math.floor((5 * doy + 2) / 153);
  const d = doy - Math.floor((153 * mp + 2) / 5) + 1;
  const m = mp < 10 ? mp + 3 : mp - 9;
  return iso(yoe + era * 400 + (m <= 2 ? 1 : 0), m, d);
}

/** Whole days since a fixed epoch, for "within a day" checks. Input is a YYYY-MM-DD string. */
export function dayNumber(date: string): number {
  const y = +date.slice(0, 4);
  const m = +date.slice(5, 7);
  const d = +date.slice(8, 10);
  const yy = m <= 2 ? y - 1 : y;
  const era = Math.floor(yy / 400);
  const yoe = yy - era * 400;
  const doy = Math.floor((153 * (m + (m > 2 ? -3 : 9)) + 2) / 5) + d - 1;
  const doe = yoe * 365 + Math.floor(yoe / 4) - Math.floor(yoe / 100) + doy;
  return era * 146097 + doe;
}

// ---- amounts ------------------------------------------------------------------------------------

export type Amount = { kind: "empty" } | { kind: "bad" } | { kind: "ok"; paise: number; negative: boolean; side: Direction | null };

/**
 * One amount cell. Handles commas (also lakh grouping), ₹/Rs/INR, parentheses and a leading or trailing minus as negative,
 * and a Dr/Cr suffix or prefix as `side`. The value is built from the digits: "1,234.50" is 123450, never 1234.5 * 100.
 * More than `digits` decimals is allowed only when the extra ones are zeros.
 */
export function parseAmount(cell: Cell | undefined, digits = 2): Amount {
  if (cell === null || cell === undefined) return { kind: "empty" };
  let s: string;
  let negative = false;
  if (typeof cell === "number") {
    if (!Number.isFinite(cell)) return { kind: "bad" };
    negative = cell < 0;
    const abs = Math.abs(cell);
    const scaled = abs * 10 ** digits;
    // A numeric cell is a float by nature; read it back through its own digits when it is a whole number of minor units.
    s = Math.abs(scaled - Math.round(scaled)) < 1e-6 ? abs.toFixed(digits) : String(abs);
  } else {
    s = textOf(cell);
    if (s === "" || /^(-+|na|n\/a|nil)$/i.test(s)) return { kind: "empty" };
  }
  let side: Direction | null = null;
  const sideM = /^(?:(dr|cr)\.?\s*)|(?:\s*(dr|cr)\.?)$/i.exec(s);
  if (sideM) {
    side = (sideM[1] ?? sideM[2])!.toLowerCase() === "dr" ? "debit" : "credit";
    s = s.replace(sideM[0], "");
  }
  s = s.replace(/₹|\brs\.?|\binr\b/gi, "").replace(/[,\s]/g, "");
  if (/^\(.*\)$/.test(s)) {
    negative = true;
    s = s.slice(1, -1);
  }
  if (s.startsWith("-")) {
    negative = true;
    s = s.slice(1);
  } else if (s.endsWith("-")) {
    negative = true;
    s = s.slice(0, -1);
  } else if (s.startsWith("+")) s = s.slice(1);
  const m = /^(\d*)(?:\.(\d*))?$/.exec(s);
  if (!m || (m[1] === "" && (m[2] ?? "") === "")) return { kind: "bad" };
  const whole = m[1] || "0";
  const frac = m[2] ?? "";
  if (frac.length > digits && /[1-9]/.test(frac.slice(digits))) return { kind: "bad" };
  const paise = Number(whole + frac.slice(0, digits).padEnd(digits, "0"));
  if (!Number.isSafeInteger(paise)) return { kind: "bad" };
  return { kind: "ok", paise, negative, side };
}

// ---- header detection -----------------------------------------------------------------------------

type Role = "date" | "narration" | "ref" | "debit" | "credit" | "amount" | "drcr" | "balance";

const norm = (c: Cell | undefined) => textOf(c).toLowerCase().replace(/[^a-z0-9/ ]+/g, " ").replace(/\s+/g, " ").trim();

/** Which statement column a header cell names. Order matters: "Withdrawal Amt." is a debit, not an amount; "Value Dt" is a date. */
export function classifyHeader(cell: Cell | undefined): Role | null {
  const h = norm(cell);
  if (h === "") return null;
  if (/balance|^bal$|\bbal\b/.test(h)) return "balance";
  if (/^(dr ?\/ ?cr|cr ?\/ ?dr|debit ?\/ ?credit|credit ?\/ ?debit|txn type|transaction type|tran type|type|cr dr|dr cr|dr or cr)$/.test(h)) return "drcr";
  if (/\bdate\b|^dt$|\bdt\b/.test(h)) return "date";
  if (/withdraw|debit|^dr$|^dr\b|paid out|money out/.test(h)) return "debit";
  if (/deposit|credit|^cr$|^cr\b|paid in|money in/.test(h)) return "credit";
  if (/amount|^amt$|\bamt\b/.test(h)) return "amount";
  if (/chq|cheque|\bref\b|reference|utr|instrument/.test(h)) return "ref";
  if (/narration|description|particulars|remarks|details|transaction/.test(h)) return "narration";
  return null;
}

/** A short stable key for a header row, so a mapping the user made is found again next month. */
export function headerSignature(row: Cell[]): string {
  const cells = row.map(norm).filter((c) => c !== "");
  let h = 0x811c9dc5;
  for (const ch of cells.join("|")) h = Math.imul(h ^ ch.charCodeAt(0), 0x01000193) >>> 0;
  return `h${cells.length}-${h.toString(16).padStart(8, "0")}`;
}

const SCAN_ROWS = 60;

interface Guess {
  headerRow: number;
  mapping: Partial<ColumnMapping>;
  complete: boolean;
}

function guessFromRow(row: Cell[], headerRow: number): Guess {
  const cols = new Map<Role, number[]>();
  row.forEach((c, i) => {
    const r = classifyHeader(c);
    if (r) cols.set(r, [...(cols.get(r) ?? []), i]);
  });
  const first = (r: Role) => cols.get(r)?.[0] ?? null;
  const dates = cols.get("date") ?? [];
  const pickedDate = dates.find((i) => /txn|tran|transaction|posting/.test(norm(row[i]))) ?? dates.find((i) => !/value/.test(norm(row[i]))) ?? dates[0] ?? null;
  const debit = first("debit");
  const credit = first("credit");
  const amount = first("amount");
  const split = debit !== null || credit !== null;
  // Kotak-style files carry a second Dr/Cr next to the balance: the one just after the amount belongs to the amount.
  const drcrs = cols.get("drcr") ?? [];
  const drcr = split ? null : (amount !== null ? (drcrs.find((i) => i > amount) ?? drcrs[0]) : drcrs[0]) ?? null;
  const mapping: Partial<ColumnMapping> = {
    headerRow,
    ...(pickedDate !== null ? { date: pickedDate } : {}),
    ...(first("narration") !== null ? { narration: first("narration")! } : {}),
    ref: first("ref"),
    debit,
    credit,
    amount: split ? null : amount,
    drcr,
    balance: first("balance"),
  };
  const complete = pickedDate !== null && first("narration") !== null && (split || amount !== null);
  return { headerRow, mapping, complete };
}

// ---- rows -----------------------------------------------------------------------------------------

const rowText = (row: Cell[]) => row.map(textOf).filter(Boolean).join(" | ").slice(0, 200);

const sideOf = (cell: Cell | undefined): Direction | null => {
  const t = norm(cell);
  if (/^(dr|debit|d|withdrawal|wdl)$/.test(t)) return "debit";
  if (/^(cr|credit|c|deposit|dep)$/.test(t)) return "credit";
  return null;
};

/** Reads every row after the header with a mapping. A row that cannot be read is reported, never dropped silently; a footer or blank line is skipped. */
export function applyMapping(grid: Grid, m: ColumnMapping, digits = 2): { txns: Txn[]; bad: BadRow[] } {
  const txns: Txn[] = [];
  const bad: BadRow[] = [];
  const split = m.debit !== null || m.credit !== null;
  for (let r = m.headerRow + 1; r < grid.length; r++) {
    const row = grid[r] ?? [];
    if (row.every(isEmpty)) continue;
    const rowNo = r + 1;
    const narration = textOf(row[m.narration]);
    const dateCell = row[m.date];
    const debit = m.debit !== null ? parseAmount(row[m.debit], digits) : ({ kind: "empty" } as Amount);
    const credit = m.credit !== null ? parseAmount(row[m.credit], digits) : ({ kind: "empty" } as Amount);
    const single = !split && m.amount !== null ? parseAmount(row[m.amount], digits) : ({ kind: "empty" } as Amount);
    const hasMoney = [debit, credit, single].some((a) => a.kind === "ok" && a.paise > 0) || [debit, credit, single].some((a) => a.kind === "bad");

    const date = parseDate(dateCell);
    if (date === null) {
      const prev = txns[txns.length - 1];
      // A wrapped narration: no date, no money, only text. It belongs to the line above.
      if (isEmpty(dateCell) && !hasMoney && narration !== "" && prev) {
        prev.description = `${prev.description} ${narration}`.trim();
        continue;
      }
      // Footers and summaries have no money in the amount columns: skip. A money row without a readable date is a real problem.
      // A label such as "Opening Balance" has no digit in its date cell; a mangled date has.
      if (hasMoney && (isEmpty(dateCell) || /\d/.test(textOf(dateCell)))) bad.push({ rowNo, reason: isEmpty(dateCell) ? "Missing date" : `Unreadable date "${textOf(dateCell).slice(0, 24)}"`, raw: rowText(row) });
      continue;
    }

    let direction: Direction;
    let paise: number;
    if (split) {
      if (debit.kind === "bad" || credit.kind === "bad") {
        bad.push({ rowNo, reason: "Unreadable amount", raw: rowText(row) });
        continue;
      }
      const d = debit.kind === "ok" ? debit.paise : 0;
      const c = credit.kind === "ok" ? credit.paise : 0;
      if (d > 0 && c > 0) {
        bad.push({ rowNo, reason: "Both debit and credit have an amount", raw: rowText(row) });
        continue;
      }
      if (d === 0 && c === 0) {
        bad.push({ rowNo, reason: "No amount", raw: rowText(row) });
        continue;
      }
      direction = d > 0 ? "debit" : "credit";
      paise = d > 0 ? d : c;
    } else {
      if (single.kind === "bad") {
        bad.push({ rowNo, reason: "Unreadable amount", raw: rowText(row) });
        continue;
      }
      if (single.kind !== "ok" || single.paise === 0) {
        bad.push({ rowNo, reason: "No amount", raw: rowText(row) });
        continue;
      }
      // Dr/Cr in its own column wins, then a suffix on the amount, then the sign: minus is money out, plus is money in.
      const side = (m.drcr !== null ? sideOf(row[m.drcr]) : null) ?? single.side;
      if (side) direction = side;
      else if (m.drcr !== null) {
        bad.push({ rowNo, reason: `Cannot tell debit from credit ("${textOf(row[m.drcr]).slice(0, 12)}")`, raw: rowText(row) });
        continue;
      } else direction = single.negative ? "debit" : "credit";
      paise = single.paise;
    }

    const refText = m.ref !== null ? textOf(row[m.ref]) : "";
    const bal = m.balance !== null ? parseAmount(row[m.balance], digits) : null;
    txns.push({
      id: `r${rowNo}`,
      rowNo,
      date,
      description: narration === "" ? "(no narration)" : narration,
      ref: refText === "" ? null : refText.replace(/^'+/, ""),
      direction,
      amount: paise,
      balance: bal && bal.kind === "ok" ? (bal.negative ? -bal.paise : bal.paise) : null,
    });
  }
  return { txns, bad };
}

// ---- whole statement --------------------------------------------------------------------------------

export type ParseOutcome =
  | { kind: "parsed"; mapping: ColumnMapping; signature: string; txns: Txn[]; bad: BadRow[]; saved: boolean }
  | { kind: "needs-mapping"; headerRow: number; signature: string; guess: Partial<ColumnMapping>; reason: string }
  | { kind: "empty" };

/**
 * Finds the header row and columns. A mapping saved for this exact header wins; else the known bank layouts are recognised by
 * their header names; else (or if the guess reads no transaction) the user maps the columns once.
 */
export function parseStatement(grid: Grid, opts: { digits?: number; saved?: Record<string, ColumnMapping> } = {}): ParseOutcome {
  const digits = opts.digits ?? 2;
  const saved = opts.saved ?? {};
  const limit = Math.min(grid.length, SCAN_ROWS);
  if (grid.every((r) => r.every(isEmpty))) return { kind: "empty" };

  for (let r = 0; r < limit; r++) {
    const row = grid[r] ?? [];
    if (row.filter((c) => !isEmpty(c)).length < 3) continue;
    const signature = headerSignature(row);
    const mapping = saved[signature];
    if (mapping) {
      const m = { ...mapping, headerRow: r };
      const read = applyMapping(grid, m, digits);
      if (read.txns.length > 0 || read.bad.length > 0) return { kind: "parsed", mapping: m, signature, ...read, saved: true };
    }
  }

  let best: Guess | null = null;
  for (let r = 0; r < limit; r++) {
    const row = grid[r] ?? [];
    if (row.filter((c) => !isEmpty(c)).length < 3) continue;
    const g = guessFromRow(row, r);
    if (g.complete) {
      const mapping = g.mapping as ColumnMapping;
      const read = applyMapping(grid, mapping, digits);
      const signature = headerSignature(row);
      if (read.txns.length > 0) return { kind: "parsed", mapping, signature, ...read, saved: false };
      best ??= g;
      continue;
    }
    const roles = Object.values(g.mapping).filter((v) => typeof v === "number").length;
    const bestRoles = best ? Object.values(best.mapping).filter((v) => typeof v === "number").length : -1;
    if (roles > bestRoles) best = g;
  }

  if (best) {
    const row = grid[best.headerRow] ?? [];
    return { kind: "needs-mapping", headerRow: best.headerRow, signature: headerSignature(row), guess: best.mapping, reason: best.complete ? "The columns were found but no transaction could be read from them." : "This layout is new to us." };
  }
  const firstWide = grid.findIndex((row) => row.filter((c) => !isEmpty(c)).length >= 3);
  const headerRow = Math.max(firstWide, 0);
  return { kind: "needs-mapping", headerRow, signature: headerSignature(grid[headerRow] ?? []), guess: { headerRow }, reason: "This layout is new to us." };
}
